// src/features/messages/panels/SharedMediaPanel.tsx
import styled from 'styled-components';
import { Panel, SectionLabel } from '@/ui';
import { Icon } from '@/icons';
import { mediaLabel } from '../meta';
import type { Conversation } from '@/core/models/message';

const Grid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;`;
// De-slopped: solid void-ramp tile + teal hairline + teal glyph — NEVER a rainbow gradient.
const ImageTile = styled.div`
  aspect-ratio: 1 / 1; border-radius: ${({ theme }) => theme.radius.sm};
  display: grid; place-items: center;
  overflow: hidden;
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.accentCyan};
`;
// A REAL attachment fills its tile — the glyph above is only the legacy
// decorative placeholder, and showing it for an actual photo would be a worse
// panel than the one that already exists in the thread.
const Thumb = styled.img`
  display: block; width: 100%; height: 100%; object-fit: cover;
`;
const VoiceTile = styled.div`
  aspect-ratio: 1 / 1; border-radius: ${({ theme }) => theme.radius.sm};
  display: grid; place-items: center;
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.accentCyan};
`;
const Empty = styled.p`
  margin: 0; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface SharedMediaPanelProps {
  convo: Conversation;
}

export function SharedMediaPanel({ convo }: SharedMediaPanelProps) {
  // `m.image` is the REAL 1.24.00 attachment; `m.media` is the legacy decorative
  // flag that only seeded demo threads ever carry. Filtering on `media` alone
  // meant a thread full of real photos still read "No shared media yet", so
  // `image` is checked FIRST here and in the tile branch below — a real photo
  // matched by a widened filter but rendered by a `media === 'image'` test would
  // come out as a microphone tile labelled "Voice message".
  const media = convo.messages.filter((m) => m.image || m.media);
  return (
    <Panel>
      <SectionLabel as="h2">Shared Media</SectionLabel>
      {media.length === 0 ? (
        <Empty>No shared media yet.</Empty>
      ) : (
        <Grid>
          {media.map((m) =>
            m.image ? (
              <ImageTile key={m.id}>
                <Thumb
                  src={m.image.url}
                  alt={m.body.trim() !== '' ? m.body : mediaLabel('image')}
                />
              </ImageTile>
            ) : m.media === 'image' ? (
              <ImageTile key={m.id} role="img" aria-label={mediaLabel('image')}>
                <Icon name="image" size={18} />
              </ImageTile>
            ) : (
              <VoiceTile key={m.id} aria-label={mediaLabel('voice')}>
                <Icon name="mic" size={18} />
              </VoiceTile>
            ),
          )}
        </Grid>
      )}
    </Panel>
  );
}
